import { ReactNode } from 'react'

import CopyIcon from '@/assets/icons/copy.svg?react'
import { Trans } from '@/components'
import { cn } from '@/utils/style'

export interface SnapshotOwnerLogEntry {
  time: string
  name: string
}

interface SnapshotDetailContentProps {
  id: string
  name: string | ReactNode
  size: string
  createTime: string
  statusLabel: string
  statusColor: string
  ownerName: string
  originOwnerName?: string
  ownerLog?: SnapshotOwnerLogEntry[]
  onCopyId: (id: string) => void
}

export function SnapshotDetailContent({
  id,
  name,
  size,
  createTime,
  statusLabel,
  statusColor,
  ownerName,
  originOwnerName,
  ownerLog,
  onCopyId,
}: SnapshotDetailContentProps) {
  return (
    <>
      {/* Section: Basic Information */}
      <div className="flex flex-col gap-4">
        {/* Title */}
        <div className="flex gap-2 items-center px-0 py-2">
          <div className="bg-btn-secondary h-5 w-1" />
          <p className="h6 text-primary">
            <Trans i18nKey="teamSnapshot.detail.basicInfo">Basic Information</Trans>
          </p>
        </div>

        {/* Data Card */}
        <div className="bg-panel-gray-100 p-4 rounded-lg flex flex-col gap-4">
          {/* Row 1: ID, Name, Size */}
          <div className="grid grid-cols-3 gap-4">
            {/* ID with Copy */}
            <div className="flex flex-col gap-1">
              <p className="t-xs text-secondary">
                <Trans i18nKey="teamSnapshot.detail.id">ID</Trans>
              </p>
              <div className="flex gap-1 items-center">
                <p className="t-sb text-primary">{id}</p>
                <button onClick={() => onCopyId(id)} className="cursor-pointer">
                  <CopyIcon className="w-4 h-4 text-highlight" />
                </button>
              </div>
            </div>

            {/* Name */}
            <div className="flex flex-col gap-1">
              <p className="t-xs text-secondary">
                <Trans i18nKey="teamSnapshot.detail.name">Name</Trans>
              </p>
              <div className="flex gap-1 items-center">
                {typeof name === 'string' ? <p className="t-sb text-primary">{name}</p> : name}
              </div>
            </div>

            {/* Size */}
            <div className="flex flex-col gap-1">
              <p className="t-xs text-secondary">
                <Trans i18nKey="teamSnapshot.detail.size">Size</Trans>
              </p>
              <p className="t-sb text-primary">{size}</p>
            </div>
          </div>

          {/* Row 2: Create Time, Status, Empty */}
          <div className="grid grid-cols-3 gap-4">
            {/* Create Time */}
            <div className="flex flex-col gap-1">
              <p className="t-xs text-secondary">
                <Trans i18nKey="teamSnapshot.detail.createTime">Create Time</Trans>
              </p>
              <p className="t-sb text-primary">{createTime}</p>
            </div>

            {/* Status */}
            <div className="flex flex-col gap-1">
              <p className="t-xs text-secondary">
                <Trans i18nKey="teamSnapshot.detail.status">Status</Trans>
              </p>
              <p className={cn('t-sb', statusColor)}>{statusLabel}</p>
            </div>

            {/* Empty */}
            <div></div>
          </div>
        </div>
      </div>

      {/* Section: Owner Information */}
      <div className="flex flex-col gap-4">
        {/* Title */}
        <div className="flex gap-2 items-center px-0 py-2">
          <div className="bg-btn-secondary h-5 w-1" />
          <p className="h6 text-primary">
            <Trans i18nKey="teamSnapshot.detail.owner">Owner</Trans>
          </p>
        </div>

        {/* Owner Card */}
        <div className="bg-panel-gray-100 p-4 rounded-lg flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-4">
            {/* Origin Owner */}
            <div className="flex flex-col gap-1">
              <p className="t-xs text-secondary">
                <Trans i18nKey="teamSnapshot.detail.originOwner">Origin Owner</Trans>
              </p>
              <p className="t-sb text-primary">{originOwnerName ?? ownerName}</p>
            </div>

            {/* Current Owner */}
            <div className="flex flex-col gap-1">
              <p className="t-xs text-secondary">
                <Trans i18nKey="teamSnapshot.detail.currentOwner">Current Owner</Trans>
              </p>
              <p className="t-sb text-primary">{ownerName}</p>
            </div>
          </div>

          {/* Owner Log timeline */}
          {ownerLog && ownerLog.length > 0 && (
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-3">
                <p className="t-xs text-secondary whitespace-nowrap">
                  <Trans i18nKey="teamSnapshot.detail.ownerLog">Owner Log</Trans>
                </p>
                <div className="h-px bg-neutral-200 flex-1" />
              </div>
              <ul className="flex flex-col">
                {ownerLog.map((entry, index) => (
                  <li key={`${entry.time}-${entry.name}`} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <span className="w-2 h-2 rounded-full bg-btn-secondary mt-1.5 shrink-0" />
                      {index < ownerLog.length - 1 && (
                        <span className="w-px flex-1 bg-neutral-200" />
                      )}
                    </div>
                    <div className="flex gap-3 pb-4">
                      <span className="t-sm text-tertiary">{entry.time}</span>
                      <span className="t-sb text-primary">{entry.name}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
